import {
  DefaultNumberSorting,
  EnumValue,
  Table,
  TableColumnType,
  useDefaultTablePaging,
  useFilters,
  useSorting,
} from '@/components';
import {
  useGetCommunityAccessesQuery,
  useGetUsersQuery,
  UserCommunityAccessDetails,
  UserState,
} from '@/redux';
import {
  useEditUserActionBase,
  UseEditUserActionBaseArgs,
} from './useEditUsersAction';
import { useCallback, useMemo } from 'react';
import { useUsersColumns } from './useUsersColumns';
import { Role, StringFilterOptions, UserFilterParams } from '@/core';
import { Alert } from 'antd';
import i18next from 'i18next';
import { useFiltersFactory } from '@/utils';
import { useTranslation } from 'react-i18next';
import { keyBy } from 'lodash';

interface Props {
  organizationId?: string | string[];
  includeUnlisted?: boolean;
  showClientName?: boolean;
}

export type ClientUserListItemType = UserState & {
  access: UserCommunityAccessDetails;
};

function useClientUsers({ organizationId }: Props) {
  const { setTotal, paging, params } = useDefaultTablePaging();

  const {
    filters,
    onFiltersChange,
    resetFilters,
    stringFilterOptions,
    setStringFilterOptions,
  } = useFilters<UserFilterParams>([
    'name',
    'email',
    'number',
    'clientFilter',
    'communitiesFilter',
  ]);

  const { sorting, onSortingChange } =
    useSorting<ClientUserListItemType>(DefaultNumberSorting);

  const { data: usersData, isFetching: isUsersLoading } = useGetUsersQuery({
    organizationId,
    onlyClient: true,
    paging: params,
    filters,
    sorting,
  });

  const users = useMemo(() => {
    if (usersData) {
      setTotal(usersData.total);
      setStringFilterOptions(usersData.stringFilterOptions);
    }

    return usersData?.items;
  }, [usersData, setTotal, setStringFilterOptions]);

  const { data: accesses, isFetching: isAccessesLoading } =
    useGetCommunityAccessesQuery(
      {
        userIds: users?.map((x) => x.id) ?? [],
      },
      {
        skip: !users?.length,
      },
    );

  const data = useMemo<ClientUserListItemType[]>(() => {
    if (!users || !accesses) {
      return [];
    }

    const accessByUserId = keyBy(accesses, (x) => x.userId);

    return users.map((x) => ({
      ...x,
      access: accessByUserId[x.id],
    }));
  }, [users, accesses]);

  return {
    data,
    stringFilterOptions,
    isLoading: isUsersLoading || isAccessesLoading,
    paging,
    onFiltersChange,
    onSortingChange,
    resetFilters,
  };
}

function useClientText() {
  return useCallback((item: ClientUserListItemType) => {
    return item.clientNames?.slice().sort().join(', ') ?? '';
  }, []);
}
function useCommunityText() {
  const { t } = useTranslation();

  return useCallback(
    (item: ClientUserListItemType) => {
      if (!item?.access) {
        return '';
      }

      return item.access.unrestricted
        ? t('all')
        : item.access.restrictedTo
            .map((x) => x.communityName)
            .sort()
            .join(', ');
    },
    [t],
  );
}

export function useClientUsersColumns(
  users: ClientUserListItemType[],
  openEdit: (id: string) => any,
  showClientName?: boolean,
  stringFilterOptions?: StringFilterOptions,
) {
  const { t } = useTranslation();

  const columns = useUsersColumns<ClientUserListItemType>(
    users,
    openEdit,
    stringFilterOptions,
  );

  const filters = useFiltersFactory(users, stringFilterOptions);

  const clientText = useClientText();
  const communityText = useCommunityText();

  return useMemo(() => {
    const roleColumn: TableColumnType<ClientUserListItemType> = {
      title: t('users.role'),
      key: 'role',
      dataIndex: 'role',
      width: 100,
      sorter: (a, b) => a.role.localeCompare(b.role),
      ...filters.enumSelect((x) => x.role, Role, [
        'CLIENT_ADMIN',
        'CLIENT_USER',
      ]),
      render: (_, record) => <EnumValue type={Role} value={record.role} />,
    };

    const clientNameColumn: TableColumnType<ClientUserListItemType> = {
      title: t('users.client'),
      key: 'client',
      width: 130,
      sorter: (a, b) => clientText(a).localeCompare(clientText(b)),
      ...filters.select((x) => clientText(x), 'clientFilter', 'include'),
      render: (_, record) => clientText(record),
    };

    const communitiesColumn: TableColumnType<ClientUserListItemType> = {
      title: t('users.communities'),
      key: 'communities',
      sorter: (a, b) => communityText(a).localeCompare(communityText(b)),
      ...filters.select(
        (x) => communityText(x),
        'communitiesFilter',
        'include',
        'All',
      ),
      render: (_, item) => communityText(item),
    };

    const result = [...columns];

    const phoneIndex = result.findIndex((x) => x.key === 'phone');

    if (showClientName) {
      result.splice(
        phoneIndex,
        0,
        roleColumn,
        clientNameColumn,
        communitiesColumn,
      );
    } else {
      result.splice(phoneIndex, 0, roleColumn, communitiesColumn);
    }

    return result;
  }, [columns, filters, t, showClientName, clientText, communityText]);
}

const EDIT_ACTION_ARGS: UseEditUserActionBaseArgs = {
  roles: ['CLIENT_ADMIN', 'CLIENT_USER'],
  noRoleSelect: false,
  formFooter: (initial, value) => {
    if (initial.role !== 'CLIENT_ADMIN' || value.role === initial.role) {
      return null;
    }

    return (
      <Alert description={i18next.t<string>('users.adminToUserEditTip')} />
    );
  },
};

export function ClientUsersListPanel(props: Props) {
  const { editModal, openEdit } = useEditUserActionBase(EDIT_ACTION_ARGS);

  const {
    data,
    isLoading,
    paging,
    stringFilterOptions,
    onFiltersChange,
    onSortingChange,
    resetFilters,
  } = useClientUsers(props);

  const columns = useClientUsersColumns(
    data,
    openEdit,
    props.showClientName,
    stringFilterOptions,
  );

  return (
    <div>
      <Table<ClientUserListItemType>
        name="Users"
        loading={isLoading}
        columns={columns}
        dataSource={data}
        rowKey={(row) => row.id}
        pagination={paging}
        onChange={(_, filters, sorting) => {
          onFiltersChange(filters);
          onSortingChange(sorting);
        }}
        onFiltersReset={resetFilters}
      />

      {editModal}
    </div>
  );
}
